import { Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import CheckRestrictions from "features/AccessManagement/CheckRestrictions"
import { ResumeLearningCard } from "features/Home/ResumeLearning"
import OverviewStatsCard from "features/LearningItems/Analytics/OverviewStatsCard"
import { useOverviewAnalytics } from "features/LearningItems/Analytics/useOverviewAnalytics"
import ForYou from "features/LearningItems/ForYou"
import { useParams } from "react-router-dom"
import routes from "routes"

import { useQualificationDetails } from ".."
import Modules from "./Modules"
import NextUp from "./NextUp"

const QualificationOverview = () => {
  const { slug, learningItemType } = useParams() as {
    slug: string
    learningItemType: string
  }
  const { qualification, accessStatus } = useQualificationDetails()

  const {
    analyticsData,
    isAnalyticsError,
    timeSpentQuery,
    selectedTimeInterval,
    setSelectedTimeInterval,
    sortedTerms,
  } = useOverviewAnalytics({
    itemId: qualification.id,
    itemType: qualification.type,
  })

  // Shares a cache entry with Modules' own getPlannerList call — this
  // doesn't add a network request.
  const { data: plannerList } = useQuery({
    enabled: Boolean(qualification.id),
    queryFn: () =>
      api.learningItems.getPlannerList({
        params: {
          learningItemType,
          learningItemId: qualification.id,
        },
      }),
    queryKey: queries.learningItems.listPlanners(qualification.id).queryKey,
  })

  const lastAccessedItem = plannerList
    ?.filter(item => item.status !== "locked" && item.lastAccessed)
    .sort(
      (a, b) =>
        new Date(b.lastAccessed as string).getTime() -
        new Date(a.lastAccessed as string).getTime()
    )[0]

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:gap-x-6">
      <div className="flex flex-col gap-[42px] lg:col-span-8">
        <OverviewStatsCard
          isCourse={false}
          analyticsData={analyticsData}
          isAnalyticsError={isAnalyticsError}
          timeSpentQuery={timeSpentQuery}
          selectedTimeInterval={selectedTimeInterval}
          setSelectedTimeInterval={setSelectedTimeInterval}
          sortedTerms={sortedTerms}
        />

        {lastAccessedItem && (
          <div>
            <Typography variant="title3" className="mb-1">
              Continue
            </Typography>
            <CheckRestrictions
              accessStatus={accessStatus}
              render={_ => (
                // Restriction is already catered inside the component
                <ResumeLearningCard
                  href={routes.parentItemCourse
                    .replace(":learningItemType", learningItemType)
                    .replace(":slug", lastAccessedItem.slug)
                    .replace(":parentSlug", slug)}
                  item={lastAccessedItem}
                  progress={lastAccessedItem.progress}
                />
              )}
            />
          </div>
        )}

        <Modules slug={slug} />
      </div>

      <div className="flex flex-col gap-4 lg:col-span-4">
        <NextUp
          learningItemId={qualification.id}
          learningItemType={qualification.type}
        />

        <ForYou />
      </div>
    </div>
  )
}

export default QualificationOverview
